import { LandingPageLayers, DesignSystem, Plugin } from "../types/ThemeTypes";
import { REACT_APP_CMS_FE_DOMAIN } from "@/config";

/**
 * Default landing page configuration
 * Based on minimalism theme defaultConfig.ts
 * This structure matches the Backend response format
 */

// Mapping for a single block's properties (flat structure)
export interface BlockPropertyDesignSystemMapping {
  [propertyName: string]: keyof DesignSystem;
}

// Default design system properties (from minimalism theme)
const defaultDesignSystem: DesignSystem = {
  background: "#ffff",
  defaultText: "#010101",
  primary: "#010101",
  textPrimary: "#ffff",
  secondary: "#ffff",
  textSecondary: "#010101",
  accent: "#ff3a46e6",
  textAccent: "#010101",
  buttonPrimary: "#010101",
  textButtonPrimary: "#ffffff",
  primaryFont: "Poppins",
  secondaryFont: "Poppins",
  textFont: "Poppins",
  accentFont: "Poppins",
};

// Default block properties for landing page blocks
const defaultMetaTagBlockProperties = {
  title: "",
  description: "",
  image: "",
  url: "",
  _overrides: {},
  _designSystemMapping: {},
};

const defaultHeaderBlockProperties = {
  borderTop: "0px",
  borderRight: "0px",
  borderBottom: "1px",
  borderLeft: "0px",
  borderColor: "#FFFFFF",
  backgroundColor: "#000000", // Black background as per design
  logoEnabled: true,
  logoImage:
    REACT_APP_CMS_FE_DOMAIN +
    "/static/img/theme/common/logo-preview-site-placeholder.png",
  logoLink: "/",
  logoHeight: "40px",
  logoHeightMobile: "30px",
  logoMaxWidth: "200px",
  logoMaxWidthMobile: "150px",
  buttonEnabled: true,
  buttonText: "Members",
  buttonFontFamily: "Montserrat",
  buttonFontSize: "24",
  buttonBold: false,
  buttonItalic: false,
  buttonUnderline: false,
  buttonBorder: "0px",
  buttonBorderColor: "#000000",
  buttonBackgroundColor: "#FFFFFF",
  buttonTextColor: "#000000",
  _overrides: {
    borderColor: true,
    backgroundColor: true,
    buttonBorderColor: true,
    buttonBackgroundColor: true,
    buttonTextColor: true,
  },
  _designSystemMapping: {} as BlockPropertyDesignSystemMapping,
};

const defaultContentBlockProperties = {
  heroImage:
    REACT_APP_CMS_FE_DOMAIN + "/static/img/theme/common/hero-horizontal.png",
  heroImageMobile:
    REACT_APP_CMS_FE_DOMAIN + "/static/img/theme/common/hero-vertical.png",
  heroBackgroundColor: "#000000", // Dark background for hero section
  borderTop: "0px",
  borderRight: "0px",
  borderBottom: "0px",
  borderLeft: "0px",
  borderColor: "#000000",
  _overrides: {
    heroBackgroundColor: true,
    borderColor: true,
  },
  _designSystemMapping: {} as BlockPropertyDesignSystemMapping,
};

const defaultFooterBlockProperties = {
  borderTop: "0px",
  borderRight: "0px",
  borderBottom: "0px",
  borderLeft: "0px",
  borderColor: "#000000",
  backgroundColor: "#000000", // Black background as per design
  menuEnabled: true,
  menuFontFamily: "Montserrat",
  menuFontSize: "12",
  menuBold: false,
  menuItalic: false,
  menuUnderline: false,
  menuFontTransform: "uppercase",
  menuTextColor: "#FFFFFF", // White text for links
  _overrides: {
    borderColor: true,
    backgroundColor: true,
    menuTextColor: true,
  },
  _designSystemMapping: {} as BlockPropertyDesignSystemMapping,
};

// Default landing page structure with blocks
const defaultLandingPage: LandingPageLayers = {
  metaTag: [
    {
      key: "metaTagBlock",
      name: "Meta Tag",
      type: "block",
      isActive: 1,
      properties: defaultMetaTagBlockProperties,
    },
  ],
  header: [
    {
      key: "HeaderBlock",
      name: "Header",
      type: "block",
      isActive: 1,
      properties: defaultHeaderBlockProperties,
    },
  ],
  body: [
    {
      type: "tab",
      tabName: "Content",
      enabled: true,
      blocks: [
        {
          key: "ContentBlock",
          name: "Content",
          type: "block",
          isActive: true,
          properties: defaultContentBlockProperties,
        },
      ],
    },
  ],
  footer: [
    {
      key: "FooterBlock",
      name: "Footer",
      type: "block",
      isActive: 1,
      properties: defaultFooterBlockProperties,
    },
  ],
};

// Default plugins (empty by default, will be populated by Backend)
const defaultPlugins: Plugin[] = [];

/**
 * Default landing page configuration
 * Use this as a template or fallback when Backend data is not available
 */
export const defaultLandingConfig: {
  landingPage: LandingPageLayers;
  designSystem: DesignSystem;
  plugins: Plugin[];
} = {
  landingPage: defaultLandingPage,
  designSystem: defaultDesignSystem,
  plugins: defaultPlugins,
};

// Export individual properties for convenience
export const defaultHeaderBlockProps = defaultHeaderBlockProperties;
export const defaultContentBlockProps = defaultContentBlockProperties;
export const defaultFooterBlockProps = defaultFooterBlockProperties;
export const defaultMetaTagBlockProps = defaultMetaTagBlockProperties;
